// Department contact form — Sales · Jobs application · Public relations.
import React, { useEffect, useRef, useState } from 'react';
import { Briefcase, CheckCircle2, Loader2, Megaphone, Paperclip, Send, TrendingUp } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ICONS = { sales: TrendingUp, jobs: Briefcase, pr: Megaphone };

const ContactForm = () => {
  const { t, lang } = useLanguage();
  const [departments, setDepartments] = useState([]);
  const [dep, setDep] = useState('sales');
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '', website: '' });
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    fetch(`${API_URL}/api/contact/departments`).then((r) => r.json()).then((d) => setDepartments(d.departments || [])).catch(() => {});
  }, []);

  const current = departments.find((d) => d.key === dep);
  const tt = (k, fb) => { const v = t(k); return !v || v === k ? fb : v; };
  const depLabel = (d) => tt(`contactForm.dep_${d.key}`, d.label);
  const depHint = (d) => tt(`contactForm.hint_${d.key}`, d.hint);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('department', dep);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append('lang', lang || 'en');
      if (file && current?.attachments) fd.append('attachment', file);
      const r = await fetch(`${API_URL}/api/contact/messages`, { method: 'POST', body: fd });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || t('contactForm.error'));
      setDone(depLabel(current || { key: dep, label: data.department }));
      setForm({ name: '', email: '', phone: '', subject: '', message: '', website: '' });
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const depButtons = departments.map((d) => {
    const Icon = ICONS[d.key] || Send;
    const active = d.key === dep;
    return (
      <button
        key={d.key}
        type="button"
        onClick={() => { setDep(d.key); setDone(null); setFile(null); }}
        className={`flex-1 min-w-[140px] text-left rounded-xl border p-4 transition-all ${active ? 'border-orange-500 bg-orange-50 shadow-md' : 'border-gray-200 bg-white hover:border-orange-300'}`}
        data-testid={`contact-dep-${d.key}`}
      >
        <Icon className={`w-5 h-5 mb-2 ${active ? 'text-orange-600' : 'text-gray-400'}`} />
        <div className="font-semibold text-gray-900">{depLabel(d)}</div>
        <div className="text-xs text-gray-500 mt-1 leading-snug">{depHint(d)}</div>
      </button>
    );
  });

  if (done) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-8 text-center border border-green-100" data-testid="contact-form-success">
        <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
        <h3 className="text-xl font-bold text-gray-900">{t('contactForm.successTitle')}</h3>
        <p className="text-gray-600 mt-2">{t('contactForm.successBody')} <b>{done}</b>.</p>
        <button onClick={() => setDone(null)} className="mt-5 px-5 py-2 rounded-full bg-orange-500 text-white font-semibold hover:bg-orange-600" data-testid="contact-form-again">
          {t('contactForm.sendAnother')}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 border border-gray-100 space-y-5" data-testid="contact-form">
      <div>
        <h3 className="text-xl font-bold text-gray-900">{t('contactForm.title')}</h3>
        <p className="text-sm text-gray-500 mt-1">{t('contactForm.subtitle')}</p>
      </div>
      <div className="flex flex-wrap gap-3">{depButtons}</div>

      <div className="grid sm:grid-cols-2 gap-4">
        <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t('contactForm.name')} className="px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 outline-none" data-testid="contact-form-name" />
        <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t('contactForm.email')} className="px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 outline-none" data-testid="contact-form-email" />
        <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t('contactForm.phone')} className="px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 outline-none" data-testid="contact-form-phone" />
        <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder={dep === 'jobs' ? t('contactForm.position') : t('contactForm.subject')} className="px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 outline-none" data-testid="contact-form-subject" />
      </div>
      <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={dep === 'jobs' ? t('contactForm.messageJobs') : t('contactForm.message')} className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 outline-none" data-testid="contact-form-message" />
      <input type="text" name="website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

      {current?.attachments && (
        <label className="flex items-center gap-3 px-4 py-3 border border-dashed border-orange-300 bg-orange-50/50 rounded-lg cursor-pointer text-sm text-gray-700" data-testid="contact-form-attachment">
          <Paperclip className="w-4 h-4 text-orange-600" />
          <span className="flex-1 truncate">{file ? file.name : t('contactForm.attachCv')}</span>
          <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,image/jpeg,image/png" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="contact-form-file" />
        </label>
      )}

      {error && <p className="text-sm text-red-600" data-testid="contact-form-error">{error}</p>}
      <button type="submit" disabled={sending} className="w-full sm:w-auto px-8 py-3 rounded-full bg-orange-500 text-white font-semibold hover:bg-orange-600 disabled:opacity-60 flex items-center justify-center gap-2" data-testid="contact-form-submit">
        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {t('contactForm.send')}
      </button>
      <p className="text-xs text-gray-400">{t('contactForm.privacy')}</p>
    </form>
  );
};

export default ContactForm;
